import { diffDaysInclusive, formatDateISO, rangeOverlapInclusive } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import type { ChecklistStatus } from '../../shared/checklistUtils';
import { formatContextDate, getStatusLabel, isFiniteNumber } from '../../shared/checklistUtils';

import type { BlackoutDateRange, SurchargeTableRow, SurchargesChecklistRow } from './types';

export function buildSurchargesRows({
  surcharges,
  travelFromDate,
  travelToDate,
}: {
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}): Array<SurchargesChecklistRow> {
  const travelPeriodData =
    travelFromDate && travelToDate ? buildTravelPeriodData(travelFromDate, travelToDate, surcharges) : null;
  const travelDays = travelPeriodData?.travelDays ?? 0;
  const surchargeDays = travelPeriodData?.surchargeDays ?? 0;
  const coveragePercent = travelPeriodData && travelDays > 0 ? (surchargeDays / travelDays) * 100 : undefined;
  const coverageStatus: ChecklistStatus = !travelPeriodData
    ? 'blocked'
    : coveragePercent !== undefined && coveragePercent > 75
      ? 'requires approval'
      : 'approved';

  const overlappingPairs: Array<string> = [];
  for (let i = 0; i < surcharges.length; i++) {
    for (let j = i + 1; j < surcharges.length; j++) {
      const a = surcharges[i];
      const b = surcharges[j];
      if (rangeOverlapInclusive(a.start_date, a.end_date, b.start_date, b.end_date)) {
        overlappingPairs.push(`${a.name} & ${b.name}`);
      }
    }
  }
  const overlapStatus: ChecklistStatus = overlappingPairs.length > 0 ? 'requires approval' : 'approved';

  return [
    {
      details: [
        { label: 'Travel period days', value: travelPeriodData ? String(travelDays) : 'N/A' },
        { label: 'Surcharge days covered', value: travelPeriodData ? String(surchargeDays) : 'N/A' },
        { label: 'Coverage', value: coveragePercent != null ? `${coveragePercent.toFixed(1)}%` : 'N/A' },
        { label: 'Travel from', value: travelFromDate ? formatContextDate(travelFromDate) : 'Not available' },
        { label: 'Travel to', value: travelToDate ? formatContextDate(travelToDate) : 'Not available' },
      ],
      field: 'Surcharge Coverage',
      id: 'coverage:offer',
      name: 'Surcharge coverage rule',
      status: coverageStatus,
      statusLabel: getStatusLabel(coverageStatus),
    },
    {
      details: [{ label: 'Overlapping pairs', value: overlappingPairs.join('; ') || 'None' }],
      field: 'Overlapping Surcharge Dates',
      id: 'overlap:offer',
      name: 'Overlapping surcharge dates',
      overlappingPairs,
      status: overlapStatus,
      statusLabel: getStatusLabel(overlapStatus),
    },
  ];
}

export function buildSurchargeTableRows({
  blackoutDates,
  packages,
  surcharges,
  travelFromDate,
  travelToDate,
}: {
  blackoutDates?: Array<BlackoutDateRange>;
  packages?: Array<App.Package>;
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}): Array<SurchargeTableRow> {
  const dealOptionNames = (packages ?? [])
    .map((offerPackage) => offerPackage.deal_option_name || offerPackage.name || '')
    .filter(Boolean);

  const travelFromDateOnly = travelFromDate ? formatDateISO(travelFromDate) : undefined;
  const travelToDateOnly = travelToDate ? formatDateISO(travelToDate) : undefined;

  return surcharges.map((surcharge) => {
    const insideTravelPeriod =
      travelFromDateOnly && travelToDateOnly
        ? formatDateISO(surcharge.start_date) >= travelFromDateOnly &&
          formatDateISO(surcharge.end_date) <= travelToDateOnly
        : null;

    const blackoutOverlaps = (blackoutDates ?? []).filter((blackoutDate) =>
      rangeOverlapInclusive(surcharge.start_date, surcharge.end_date, blackoutDate.from__c, blackoutDate.to__c),
    );
    const blackoutOverlap = blackoutDates ? blackoutOverlaps.length > 0 : null;

    const needsReview =
      surcharge.accumulate ||
      (insideTravelPeriod !== null && !insideTravelPeriod) ||
      (blackoutOverlap !== null && blackoutOverlap);
    const status: ChecklistStatus = needsReview ? 'requires approval' : 'approved';

    return {
      id: `table:surcharge__c:${String(surcharge.id)}`,
      name: surcharge.name,
      fromDate: surcharge.start_date,
      toDate: surcharge.end_date,
      convertedCost: isFiniteNumber(surcharge.converted_cost) ? surcharge.converted_cost : undefined,
      surchargePrice: isFiniteNumber(surcharge.sell_price) ? surcharge.sell_price : undefined,
      currencyCode: surcharge.currency_code || 'AUD',
      accumulate: surcharge.accumulate,
      insideTravelPeriod,
      blackoutOverlap,
      blackoutOverlaps,
      dealOptionNames,
      needsReview,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function buildTravelPeriodData(
  travelFromDate: string,
  travelToDate: string,
  surcharges: Array<SalesforceSurchargePeriod>,
) {
  const travelDays = daysBetween(travelFromDate, travelToDate);
  const travelFromDateOnly = formatDateISO(travelFromDate);
  const travelToDateOnly = formatDateISO(travelToDate);
  const clampedRanges = surcharges
    .map((surcharge) => {
      const start = formatDateISO(surcharge.start_date);
      const end = formatDateISO(surcharge.end_date);
      return {
        start: start < travelFromDateOnly ? travelFromDateOnly : start,
        end: end > travelToDateOnly ? travelToDateOnly : end,
      };
    })
    .filter((range) => range.start <= range.end);
  const surchargeDays = countUnionDays(clampedRanges);
  return { travelDays, surchargeDays };
}

function daysBetween(from: string, to: string): number {
  return Math.max(0, diffDaysInclusive(from, to));
}

function countUnionDays(ranges: Array<{ start: string; end: string }>): number {
  if (ranges.length === 0) {
    return 0;
  }

  const sorted = [...ranges].sort((a, b) => (a.start < b.start ? -1 : 1));
  let totalDays = 0;
  let currentStart = sorted[0].start;
  let currentEnd = sorted[0].end;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start <= currentEnd) {
      if (sorted[i].end > currentEnd) {
        currentEnd = sorted[i].end;
      }
    } else {
      totalDays += daysBetween(currentStart, currentEnd);
      currentStart = sorted[i].start;
      currentEnd = sorted[i].end;
    }
  }

  return totalDays + daysBetween(currentStart, currentEnd);
}
